import { CSSProperties, useEffect, useState } from "react";

import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";

import searchBoxControlStyle from "./SearchBoxControl.module.css";

interface ISearchBoxControlProps {
    searchText: string;
    isFixed?: boolean;
    container: HTMLElement;
    elementsRects: DOMRect[];
    currentScrollTop: number;
    rightInPX?: number;
    topInPX?: number;
    encounterSearchTermChangedOperation: number;
    scrollToRatio: number;
    isWindow?: boolean;
    setHighlightServiceIndexCallbackFunc: (curIndex: number) => void;
}

function SearchBoxControl(props: ISearchBoxControlProps): JSX.Element {
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const containerStyle: CSSProperties = { position: props.isFixed ? "fixed" : "absolute", right: props.rightInPX + "px", top: props.topInPX + "px" };

    function scrollTo(nextIndex: number): void {
        const targetElement = props.elementsRects[nextIndex];
        const container = props.container;
        const scrollToRatio = props.scrollToRatio ? props.scrollToRatio : 0.1;

        if (targetElement && container) {
            const windowScrollTop = props?.isWindow ? container.scrollTop : 0;
            const containerY = container.getClientRects()[0]?.y ?? windowScrollTop;
            const containerClientHeightWithRatio = Math.floor(container.clientHeight / scrollToRatio);
            const nextYPosition = targetElement.y - (containerY + windowScrollTop) - containerClientHeightWithRatio + props.currentScrollTop;

            container.scrollTo(0, nextYPosition);
            props.setHighlightServiceIndexCallbackFunc(nextIndex);
            setCurrentIndex(nextIndex);
        }
    }

    useEffect(() => {
        setCurrentIndex(0);
        setIsVisible(true);
    }, [props.searchText]);

    useEffect(() => {
        const elementsLength = props.elementsRects?.length ? Math.max(props.elementsRects.length - 1, 0) : 0;
        setCurrentIndex(prev => Math.min(prev, elementsLength));
    }, [props.elementsRects]);

    return (
        isVisible
            ? (
                <div
                    role="searchbox"
                    className={searchBoxControlStyle.openContainer}
                    style={containerStyle}
                >
                    <div className={`${searchBoxControlStyle.FindInputContainer ?? ""} push-right-5`}>
                        <TextboxControl
                            debounceMs={400}
                            value={props.searchText}
                            operation={props.encounterSearchTermChangedOperation}
                        />
                    </div>
                    <div className={searchBoxControlStyle.verticalCenter}>
                        <span className="push-right-5">
                            {props.elementsRects && props.elementsRects.length > 0
                                ? `${currentIndex + 1}/${props.elementsRects?.length ?? 0}`
                                : cacheStore.GetString("NoResultsFoundLabel")
                            }
                        </span>
                        <button
                            onClick={() => scrollTo(Math.max(currentIndex - 1, 0))}
                            disabled={!(props.elementsRects?.length && currentIndex >= 0)}
                            className={searchBoxControlStyle.transparentButton}
                        >
                            <svg width="16" height="9" viewBox="0 0 16 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1.75 7.5L8 1.25L14.25 7.5" stroke="#1E355C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <button
                            onClick={() => scrollTo(Math.min(currentIndex + 1, props.elementsRects.length - 1))}
                            disabled={!(props.elementsRects?.length && currentIndex <= (props.elementsRects.length - 1))}
                            className={searchBoxControlStyle.transparentButton}
                        >
                            <svg width="16" height="9" viewBox="0 0 16 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14.25 1.5L8 7.75L1.75 1.5" stroke="#1E355C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                    <button
                        onClick={() => setIsVisible(false)}
                        className={searchBoxControlStyle.transparentButton}
                    >
                        <span className={searchBoxControlStyle.closeButtonContainer}>
                            <svg className={searchBoxControlStyle.absolute} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L13 13" stroke="#7F7F7F" strokeWidth="2" />
                            </svg>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 13L13 1" stroke="#7F7F7F" strokeWidth="2" />
                            </svg>
                        </span>
                    </button>
                </div>
            )
            : (
                <div
                    className={searchBoxControlStyle.closedContainer}
                    style={containerStyle}
                >
                    <span
                        className="glyphicon glyphicon-chevron-down"
                        onClick={() => setIsVisible(true)}
                    />
                </div>
            )
    );
}

export { SearchBoxControl };
